import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { newId } from '../api.js';
import { useMe } from '../App.jsx';
import { CameraIcon, ErrorBox, ImageIcon, JobPage, VideoIcon } from '../components.jsx';
import { isImage, isSupported, isVideo, mimeOf, prepare, prettySize } from '../lib/media.js';
import { enqueue } from '../lib/outbox.js';

export function Thumb({ file, onRemove }) {
  const url = useMemo(() => (isImage(file) ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  return (
    <div className="relative aspect-square overflow-hidden rounded-xl bg-steel/20">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-1 p-2 text-center text-xs text-steel">
          {isVideo(file) ? <VideoIcon className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
          <span>{prettySize(file.size)}</span>
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-charcoal/80 text-lg leading-none text-white"
        aria-label="Remove"
      >
        ×
      </button>
    </div>
  );
}

function Form({ job }) {
  const { maxUploadMb } = useMe();
  const nav = useNavigate();
  const [sid] = useState(newId);
  const [files, setFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState(null);
  const [preparing, setPreparing] = useState(null); // { done, total }
  const photoCam = useRef(null);
  const videoCam = useRef(null);
  const gallery = useRef(null);

  function add(list) {
    const rejected = [];
    const ok = [];
    for (const f of list) {
      if (!isSupported(f)) rejected.push(`${f.name} (not a photo or video)`);
      else if (f.size > maxUploadMb * 1024 * 1024) rejected.push(`${f.name} (over ${maxUploadMb} MB)`);
      else ok.push(f);
    }
    setError(rejected.length ? `Couldn't add: ${rejected.join(', ')}` : null);
    setFiles((cur) => [...cur, ...ok]);
  }

  async function send() {
    setPreparing({ done: 0, total: files.length });
    const items = [];
    for (let i = 0; i < files.length; i++) {
      const { blob, mime } = await prepare(files[i]);
      items.push({
        id: newId(), kind: 'file', jobId: job.id, jobName: job.name, sid,
        name: files[i].name || `file-${i + 1}`, blob, mime: mime || mimeOf(files[i]),
      });
      setPreparing({ done: i + 1, total: files.length });
    }
    items.push({ id: newId(), kind: 'complete', jobId: job.id, jobName: job.name, sid, caption: caption.trim() });
    await enqueue(items);
    nav(`/job/${job.id}/done?what=files&sid=${sid}&n=${files.length}`, { replace: true });
  }

  // Android only opens the camera when the input asks for ONE type (image/* or
  // video/*) -- a mixed accept list falls back to the file picker. So photo and
  // video each get their own camera input; the gallery input stays mixed (on
  // iPhone that one also offers "Take Photo or Video" in its own sheet).
  const input = (ref, props) => (
    <input
      ref={ref}
      type="file"
      accept="image/*,video/*"
      className="hidden"
      onChange={(e) => { add([...e.target.files]); e.target.value = ''; }}
      {...props}
    />
  );

  return (
    <>
      {input(photoCam, { accept: 'image/*', capture: 'environment' })}
      {input(videoCam, { accept: 'video/*', capture: 'environment' })}
      {input(gallery, { multiple: true })}

      <div className="grid grid-cols-2 gap-3">
        <button type="button" className="btn-primary flex-col !py-5" onClick={() => photoCam.current.click()}>
          <CameraIcon className="h-9 w-9" /> Take photo
        </button>
        <button type="button" className="btn-primary flex-col !py-5" onClick={() => videoCam.current.click()}>
          <VideoIcon className="h-9 w-9" /> Record video
        </button>
      </div>
      <button type="button" className="btn-ghost w-full" onClick={() => gallery.current.click()}>
        <ImageIcon className="h-6 w-6" /> Choose from phone
      </button>

      {error && <ErrorBox>{error}</ErrorBox>}

      {files.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {files.map((f, i) => <Thumb key={`${f.name}-${i}-${f.size}`} file={f} onRemove={() => setFiles(files.filter((_, j) => j !== i))} />)}
          </div>
          <div>
            <label className="label" htmlFor="cap">Comment (optional)</label>
            <textarea id="cap" className="input" rows={2} maxLength={500} placeholder="e.g. Before prep, north wall" value={caption} onChange={(e) => setCaption(e.target.value)} />
          </div>
          <button className="btn-primary w-full" disabled={!!preparing} onClick={send}>
            {preparing ? `Getting ready… ${preparing.done}/${preparing.total}` : `Upload ${files.length} file${files.length === 1 ? '' : 's'}`}
          </button>
        </>
      )}
    </>
  );
}

export default function AddFiles() {
  return <JobPage title="Add photos / videos">{(job) => <Form job={job} />}</JobPage>;
}
